/**
 * serviceComponentHole 对外入口。
 *
 * 提供 initMultiComponent(params) → publicApi。
 * publicApi 列表见 spec §8.3 / §17.5。
 *
 * 注意：在调用 initMultiComponent 之前，必须先 import 至少一个 types/*.js，
 * 触发 registerComponentType 副作用。本文件统一在底部 import 所有已实现的 type。
 */

import { Toast, Store, debugLog } from '@/utils/common.js';

import { DRAW_TYPE, modeEdit } from './constants.js';
import {
  getCustomSkuBackendBounds,
  getCustomSkuOutlineBounds,
  getCustomSkuMinOutlineBounds,
  convertCanvasToBackend,
  rectCanvasToApiSlotRect,
  validateInitParams,
  initSharedCanvasState
} from './core.js';
import { createOrchestrator } from './orchestrator.js';

// --- 注册所有已实现的 type 策略（副作用 import）---
import './types/keyHole.js';
import './types/liPai.js';
import './types/perforation.js';
import './types/baseHole.js';

/**
 * 按高度百分比计算底座孔圆角半径。
 * @param {number} width - 底座孔宽度 px
 * @param {number} height - 底座孔高度 px
 * @param {number} percent - 以高度为基准的百分比
 * @returns {number}
 */
function computeBaseHoleCornerRadius(width, height, percent) {
  const value = typeof percent === 'number' && isFinite(percent) ? percent : 0;
  const raw = height * (Math.max(0, value) / 100);
  return Math.max(0, Math.min(raw, width / 2, height / 2));
}

/**
 * 深拷贝可序列化数据，避免调试输出被后续运行态修改影响。
 * @param {*} value - 待拷贝的数据
 * @returns {*} 拷贝后的数据
 */
function cloneDebugValue(value) {
  // 调试快照允许字段为空，避免 JSON.stringify(undefined) 再 parse 报错。
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

/**
 * 计算 canvas 坐标转后端坐标时的 SKU 补边偏移。
 * @param {Object} sharedState - 共享状态
 * @returns {{imageRatio:number,skuRatio:number,offsetX:number,offsetY:number}}
 */
function getDebugBackendOffset(sharedState) {
  const imageRatio = sharedState.imageInfo.width / sharedState.imageInfo.height;
  const skuRatio =
    sharedState.template.paperWidth / sharedState.template.paperHeight;
  let offsetX = 0;
  let offsetY = 0;
  // 图片比 SKU 更宽时，后端坐标需要补上下方向的适配留白。
  if (imageRatio > skuRatio) {
    offsetY =
      (sharedState.imageInfo.width / skuRatio - sharedState.imageInfo.height) /
      2;
  } else {
    offsetX =
      (sharedState.imageInfo.height * skuRatio - sharedState.imageInfo.width) /
      2;
  }
  return { imageRatio, skuRatio, offsetX, offsetY };
}

/**
 * 生成多组件调试快照，用于排查插口坐标与接口 slotRect 换算问题。
 * @param {Object} orchestrator - 编排器实例
 * @param {Object} sharedState - 共享状态
 * @returns {Object} 可 JSON.stringify 的调试对象
 */
function buildDebugSnapshot(orchestrator, sharedState) {
  const components = orchestrator.getComponents();
  const backendOffset = getDebugBackendOffset(sharedState);
  const ratioBase = sharedState.imageRatioOver
    ? sharedState.imageInfo.width / backendOffset.skuRatio
    : sharedState.imageInfo.height / backendOffset.skuRatio;

  return {
    time: new Date().toISOString(),
    mode: sharedState.mode,
    imageBorderParseParams: cloneDebugValue(sharedState.imageBorderParseParams),
    imageBorderParseRect: cloneDebugValue(sharedState.imageBorderParseRect),
    template: cloneDebugValue(sharedState.template),
    imageInfo: cloneDebugValue(sharedState.imageInfo),
    canvas: {
      cssWidth: sharedState.canvasWidth,
      cssHeight: sharedState.canvasHeight,
      width: sharedState.canvas ? sharedState.canvas.width : null,
      height: sharedState.canvas ? sharedState.canvas.height : null,
      styleWidth: sharedState.canvas ? sharedState.canvas.style.width : '',
      styleHeight: sharedState.canvas ? sharedState.canvas.style.height : '',
      dpr: sharedState.dpr,
      canvasScale: sharedState.canvasScale,
      canvasRoundWhiteSpace: sharedState.canvasRoundWhiteSpace
    },
    scale: {
      imageRatioOver: sharedState.imageRatioOver,
      imageScale: sharedState.imageScale,
      maskFitScale: sharedState.maskFitScale,
      mmToPixelRatio: sharedState.mmToPixelRatio,
      skuTemplateBaseMM: sharedState.skuTemplateBaseMM,
      backendOffset,
      ratioBase
    },
    imageBoundsInCanvas: {
      left: sharedState.imageLeft,
      right: sharedState.imageRight,
      top: sharedState.imageTop,
      bottom: sharedState.imageBottom,
      width: sharedState.drawImageWidth,
      height: sharedState.drawImageHeight
    },
    outline: {
      count: sharedState.outlinePoints ? sharedState.outlinePoints.length : 0,
      firstFive: cloneDebugValue((sharedState.outlinePoints || []).slice(0, 5)),
      minX: Math.min.apply(
        null,
        (sharedState.outlinePoints || []).map(function (p) {
          return p[0];
        })
      ),
      maxX: Math.max.apply(
        null,
        (sharedState.outlinePoints || []).map(function (p) {
          return p[0];
        })
      ),
      minY: Math.min.apply(
        null,
        (sharedState.outlinePoints || []).map(function (p) {
          return p[1];
        })
      ),
      maxY: Math.max.apply(
        null,
        (sharedState.outlinePoints || []).map(function (p) {
          return p[1];
        })
      )
    },
    components: components.map(function (item) {
      const data = cloneDebugValue(item);
      // 立牌组件额外输出插口坐标在 canvas、后端坐标和接口比例中的各阶段结果。
      if (item.type === DRAW_TYPE.LI_PAI && item.slotRect) {
        const slotRect = item.slotRect;
        const slotCenter = {
          x: slotRect.x + slotRect.w / 2,
          y: slotRect.y + slotRect.h / 2
        };
        const topLeftBackend = convertCanvasToBackend(
          { x: slotRect.x, y: slotRect.y },
          sharedState
        );
        const centerBackend = convertCanvasToBackend(slotCenter, sharedState);
        const widthBackend = Math.round(slotRect.w / sharedState.canvasScale);
        const heightBackend = Math.round(slotRect.h / sharedState.canvasScale);
        const apiSlotRect = rectCanvasToApiSlotRect(slotRect, sharedState);
        // API slotRect 后两位需要保留当前规格的物理宽高，便于对照真实请求。
        if (
          item.geometryConfig &&
          typeof item.geometryConfig.slotWidthMm === 'number' &&
          typeof item.geometryConfig.slotHeightMm === 'number'
        ) {
          apiSlotRect[4] = item.geometryConfig.slotWidthMm;
          apiSlotRect[5] = item.geometryConfig.slotHeightMm;
        }
        data.debugCoordinate = {
          slotTopLeftCanvas: { x: slotRect.x, y: slotRect.y },
          slotCenterCanvas: slotCenter,
          slotSizeCanvas: { width: slotRect.w, height: slotRect.h },
          slotTopLeftBackend: topLeftBackend,
          slotCenterBackend: centerBackend,
          slotSizeBackend: { width: widthBackend, height: heightBackend },
          apiSlotRect,
          formula: {
            xSource: 'slotRect.x',
            ySource: 'slotRect.y',
            canvasToBackend:
              'backend = Math.round(canvas / canvasScale + offset)',
            percent:
              'x/width = backendX/backendWidth / imageInfo.width; y/height = backendY/backendHeight / imageInfo.height'
          }
        };
      }
      return data;
    }),
    exportedLayerDataPatch: cloneDebugValue(orchestrator.exportLayerDataPatch())
  };
}

/**
 * 构建圆角矩形路径。
 * @param {CanvasRenderingContext2D} ctx - canvas 上下文
 * @param {number} x - 左上角 x
 * @param {number} y - 左上角 y
 * @param {number} w - 宽度
 * @param {number} h - 高度
 * @param {number} radius - 圆角半径
 * @returns {void}
 */
function buildRoundRectPath(ctx, x, y, w, h, radius) {
  const r = Math.max(0, Math.min(radius || 0, w / 2, h / 2));
  ctx.beginPath();
  // 无圆角时退化为普通矩形
  if (r <= 0) {
    ctx.rect(x, y, w, h);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * 加载图片元素。
 * @param {string} src - 图片地址
 * @returns {Promise<HTMLImageElement>}
 */
function loadImageElement(src) {
  return new Promise(function (resolve, reject) {
    var img = new Image();
    img.onload = function () {
      resolve(img);
    };
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * 将立牌场景非 SKU 比例的 mask 补黑边归一化为 SKU 比例。
 * @param {string} maskSrc - 原始 mask 地址
 * @param {number} skuRatio - SKU 图片宽高比
 * @returns {Promise<string>} 归一化后的 mask blobUrl；已是 SKU 比例时返回原地址
 */
async function normalizeMaskToSkuRatio(maskSrc, skuRatio) {
  var maskImg = await loadImageElement(maskSrc);
  var sourceW = maskImg.naturalWidth || maskImg.width;
  var sourceH = maskImg.naturalHeight || maskImg.height;

  // mask 或 SKU 比例异常时保持原图，避免生成无效 canvas。
  if (!sourceW || !sourceH || !skuRatio) {
    return maskSrc;
  }

  var sourceRatio = sourceW / sourceH;

  // 原 mask 已经是 SKU 比例时不重复导出，减少无意义的 blobUrl。
  if (Math.abs(sourceRatio - skuRatio) < 0.001) {
    return maskSrc;
  }

  var targetW;
  var targetH;
  var drawX;
  var drawY;

  // 原 mask 更宽时保持宽度不变，通过补上下黑边归一化比例。
  if (sourceRatio > skuRatio) {
    targetW = sourceW;
    targetH = Math.round(sourceW / skuRatio);
    drawX = 0;
    drawY = Math.round((targetH - sourceH) / 2);
  } else {
    targetH = sourceH;
    targetW = Math.round(sourceH * skuRatio);
    drawX = Math.round((targetW - sourceW) / 2);
    drawY = 0;
  }

  var canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  var ctx = canvas.getContext('2d');
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(maskImg, drawX, drawY, sourceW, sourceH);

  return new Promise(function (resolve, reject) {
    canvas.toBlob(function (blob) {
      // canvas 导出失败时抛错，避免后续使用空 mask。
      if (!blob) {
        reject(new Error('normalizeMaskToSkuRatio canvas.toBlob 失败'));
        return;
      }
      resolve(URL.createObjectURL(blob));
    }, 'image/png');
  });
}

/**
 * 临时修复立牌 mask：裁剪保留所有白色像素的最小矩形，再补黑边归一化为 SKU 比例。
 * @param {string} maskSrc - 原始 mask 地址
 * @param {number} skuRatio - SKU 图片宽高比
 * @returns {Promise<string>} 修复后的 mask blobUrl
 */
async function cropWhiteMaskToSkuRatio(maskSrc, skuRatio) {
  var maskImg = await loadImageElement(maskSrc);
  var sourceW = maskImg.naturalWidth || maskImg.width;
  var sourceH = maskImg.naturalHeight || maskImg.height;

  // mask 或 SKU 比例异常时保持原图，避免生成无效 canvas。
  if (!sourceW || !sourceH || !skuRatio) {
    return maskSrc;
  }

  var sourceCanvas = document.createElement('canvas');
  sourceCanvas.width = sourceW;
  sourceCanvas.height = sourceH;
  var sourceCtx = sourceCanvas.getContext('2d');
  sourceCtx.drawImage(maskImg, 0, 0, sourceW, sourceH);

  var imageData = sourceCtx.getImageData(0, 0, sourceW, sourceH);
  var data = imageData.data;
  var minX = sourceW;
  var minY = sourceH;
  var maxX = -1;
  var maxY = -1;

  for (var y = 0; y < sourceH; y++) {
    for (var x = 0; x < sourceW; x++) {
      var index = (y * sourceW + x) * 4;
      var r = data[index];
      var g = data[index + 1];
      var b = data[index + 2];
      var a = data[index + 3];
      // 近白像素视为 mask 保留区域，避免边缘抗锯齿导致漏裁。
      if (a > 0 && r >= 250 && g >= 250 && b >= 250) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  // 没有白色保留区域时保持原图，避免生成空 mask。
  if (maxX < minX || maxY < minY) {
    return maskSrc;
  }

  var cropW = maxX - minX + 1;
  var cropH = maxY - minY + 1;
  var cropRatio = cropW / cropH;
  var targetW;
  var targetH;
  var drawX;
  var drawY;

  // 白色区域更宽时保持宽度不变，通过补上下黑边归一化比例。
  if (cropRatio > skuRatio) {
    targetW = cropW;
    targetH = Math.round(cropW / skuRatio);
    drawX = 0;
    drawY = Math.round((targetH - cropH) / 2);
  } else {
    targetH = cropH;
    targetW = Math.round(cropH * skuRatio);
    drawX = Math.round((targetW - cropW) / 2);
    drawY = 0;
  }

  var targetCanvas = document.createElement('canvas');
  targetCanvas.width = targetW;
  targetCanvas.height = targetH;
  var targetCtx = targetCanvas.getContext('2d');
  targetCtx.fillStyle = '#000000';
  targetCtx.fillRect(0, 0, targetW, targetH);
  targetCtx.drawImage(
    sourceCanvas,
    minX,
    minY,
    cropW,
    cropH,
    drawX,
    drawY,
    cropW,
    cropH
  );

  return new Promise(function (resolve, reject) {
    targetCanvas.toBlob(function (blob) {
      // canvas 导出失败时抛错，避免后续使用空 mask。
      if (!blob) {
        reject(new Error('cropWhiteMaskToSkuRatio canvas.toBlob 失败'));
        return;
      }
      resolve(URL.createObjectURL(blob));
    }, 'image/png');
  });
}

/**
 * 获取完整刀线在物理 canvas 坐标下的边界。
 * @param {Object} sharedState - 共享状态
 * @returns {{left:number,top:number,right:number,bottom:number}|null}
 */
function getOutlinePhysicalBounds(sharedState) {
  const points = sharedState.outlinePoints || [];
  if (!points.length) return null;
  var dpr = sharedState.dpr || 1;
  var ws = sharedState.canvasRoundWhiteSpace * dpr;
  var pad = (Math.max(sharedState.lineWidth || 1, 1) * dpr) / 2;
  var minX = Infinity;
  var minY = Infinity;
  var maxX = -Infinity;
  var maxY = -Infinity;
  for (var i = 0; i < points.length; i++) {
    var x = points[i][0] * dpr + ws;
    var y = points[i][1] * dpr + ws;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return {
    left: minX - pad,
    top: minY - pad,
    right: maxX + pad,
    bottom: maxY + pad
  };
}

/**
 * 获取导出画布与主体绘制矩形信息，与 serviceHoleNew.getOutlineSourceCanvasInfo 对齐。
 * 注意：新版 dpr 不再恒为 1，cropBounds 使用物理像素，因此 subjectRectInSourceCanvas 也输出物理像素。
 * @param {Object} sharedState - 共享状态
 * @returns {{sourceCanvasSize:{width:number,height:number},imageScale:number,canvasScale:number,subjectRectInSourceCanvas:{x:number,y:number,width:number,height:number}}}
 */
function getOutlineSourceCanvasInfo(sharedState) {
  var dpr = sharedState.dpr || 1;
  return {
    sourceCanvasSize: {
      width: sharedState.canvas ? sharedState.canvas.width : 0,
      height: sharedState.canvas ? sharedState.canvas.height : 0
    },
    imageScale: sharedState.imageScale || 1,
    canvasScale: sharedState.canvasScale || 1,
    subjectRectInSourceCanvas: {
      x:
        ((sharedState.imageLeft || 0) +
          (sharedState.canvasRoundWhiteSpace || 0)) *
        dpr,
      y:
        ((sharedState.imageTop || 0) +
          (sharedState.canvasRoundWhiteSpace || 0)) *
        dpr,
      width: (sharedState.drawImageWidth || 0) * dpr,
      height: (sharedState.drawImageHeight || 0) * dpr
    }
  };
}

/**
 * 合并物理坐标边界。
 * @param {{left:number,top:number,right:number,bottom:number}|null} a - 已有边界
 * @param {{left:number,top:number,right:number,bottom:number}|null} b - 待合并边界
 * @returns {{left:number,top:number,right:number,bottom:number}|null}
 */
function mergePhysicalBounds(a, b) {
  if (!a) return b;
  if (!b) return a;
  return {
    left: Math.min(a.left, b.left),
    top: Math.min(a.top, b.top),
    right: Math.max(a.right, b.right),
    bottom: Math.max(a.bottom, b.bottom)
  };
}

/**
 * 判断物理边界是否为有效数字。
 * @param {{left:number,top:number,right:number,bottom:number}|null} bounds - 待校验边界
 * @returns {boolean} true 表示边界四个方向均为有效数字
 */
function isValidPhysicalBounds(bounds) {
  return (
    !!bounds &&
    isFinite(bounds.left) &&
    isFinite(bounds.top) &&
    isFinite(bounds.right) &&
    isFinite(bounds.bottom)
  );
}

/**
 * 获取孔位区域在物理 canvas 坐标下的边界。
 * @param {Object} sharedState - 共享状态
 * @param {Array<Object>} holeRegions - 孔位区域
 * @returns {{left:number,top:number,right:number,bottom:number}|null}
 */
function getHoleRegionsPhysicalBounds(sharedState, holeRegions) {
  if (!holeRegions || !holeRegions.length) return null;
  var dpr = sharedState.dpr || 1;
  var ws = sharedState.canvasRoundWhiteSpace * dpr;
  var pad = (Math.max(sharedState.lineWidth || 1, 1) * dpr) / 2;
  var bounds = null;
  for (var i = 0; i < holeRegions.length; i++) {
    var h = holeRegions[i];
    var cx = h.cx * dpr + ws;
    var cy = h.cy * dpr + ws;
    var current = null;
    if (h.kind === 'rotatedRect') {
      var diag = (Math.sqrt(h.w * h.w + h.h * h.h) * dpr) / 2;
      current = {
        left: cx - diag - pad,
        top: cy - diag - pad,
        right: cx + diag + pad,
        bottom: cy + diag + pad
      };
    } else if (h.radius != null) {
      var r = h.radius * dpr;
      current = {
        left: cx - r - pad,
        top: cy - r - pad,
        right: cx + r + pad,
        bottom: cy + r + pad
      };
    }
    bounds = mergePhysicalBounds(bounds, current);
  }
  return bounds;
}

/**
 * 根据刀线点和孔位区域计算导出裁剪边界。
 * @param {Object} sharedState - 共享状态
 * @param {Array<Object>} holeRegions - 孔位区域
 * @returns {Object}
 */
function getExportCropBounds(sharedState, holeRegions) {
  var bounds = mergePhysicalBounds(
    getOutlinePhysicalBounds(sharedState),
    getHoleRegionsPhysicalBounds(sharedState, holeRegions)
  );
  if (!bounds) {
    throw new Error('exportImage 缺少 outlinePoints，无法计算裁剪边界');
  }
  // 裁剪边界必须全部为有效数字，否则继续计算会污染导出 canvas 尺寸。
  if (!isValidPhysicalBounds(bounds)) {
    throw new Error('exportImage 裁剪边界包含非法数值');
  }
  var canvasWidth = sharedState.canvas.width;
  var canvasHeight = sharedState.canvas.height;
  // 主 canvas 尺寸必须有效，否则无法计算导出裁剪区域。
  if (!isFinite(canvasWidth) || !isFinite(canvasHeight)) {
    throw new Error('exportImage canvas 尺寸非法');
  }
  var leftWidth = Math.min(canvasWidth, Math.max(0, Math.floor(bounds.left)));
  var topWidth = Math.min(canvasHeight, Math.max(0, Math.floor(bounds.top)));
  var rightX = Math.min(canvasWidth, Math.max(0, Math.ceil(bounds.right)));
  var bottomY = Math.min(canvasHeight, Math.max(0, Math.ceil(bounds.bottom)));
  // 极端异常边界无法形成有效裁剪区域时，直接抛错阻止错误导出。
  if (rightX <= leftWidth || bottomY <= topWidth) {
    throw new Error('exportImage 裁剪边界无效');
  }
  return {
    leftWidth,
    topWidth,
    rightWidth: Math.max(0, canvasWidth - rightX),
    bottomWidth: Math.max(0, canvasHeight - bottomY)
  };
}

/**
 * 将 imageBorderParse 返回的 rect 统一成业务矩形对象。
 * @param {Array<number>|Object|null} rect - 后端返回的 rect
 * @returns {{x:number,y:number,width:number,height:number}|null} 业务矩形
 */
function imageBorderParseRectToMaskRect(rect) {
  // rect 缺失时无法确定基础 mask 外轮廓。
  if (!rect) return null;
  if (Array.isArray(rect) && rect.length >= 4) {
    return {
      x: rect[0],
      y: rect[1],
      width: rect[2],
      height: rect[3]
    };
  }
  return {
    x: rect.x || 0,
    y: rect.y || 0,
    width: rect.width || rect.w || 0,
    height: rect.height || rect.h || 0
  };
}

/**
 * 计算后端业务坐标与画布坐标之间的补边偏移。
 * @param {Object} sharedState - 共享状态
 * @returns {{offsetX:number,offsetY:number}}
 */
function getBackendCoordinateOffset(sharedState) {
  var imageRatio = sharedState.imageInfo.width / sharedState.imageInfo.height;
  var skuRatio =
    sharedState.template.paperWidth / sharedState.template.paperHeight;
  var offsetX = 0;
  var offsetY = 0;
  // 图片比 SKU 更宽时，后端业务坐标会包含上下补边。
  if (imageRatio > skuRatio) {
    offsetY =
      (sharedState.imageInfo.width / skuRatio - sharedState.imageInfo.height) /
      2;
  } else {
    offsetX =
      (sharedState.imageInfo.height * skuRatio - sharedState.imageInfo.width) /
      2;
  }
  return { offsetX, offsetY };
}

/**
 * 将业务矩形转换到 serviceComponentHole 原始 canvas 物理坐标系。
 * @param {Object} rect - 业务坐标矩形
 * @param {Object} sharedState - 共享状态
 * @returns {{x:number,y:number,width:number,height:number}} 原始 canvas 物理坐标矩形
 */
function backendRectToSourceCanvasRect(rect, sharedState) {
  var offset = getBackendCoordinateOffset(sharedState);
  var dpr = sharedState.dpr || 1;
  var scale = sharedState.canvasScale || 1;
  var ws = (sharedState.canvasRoundWhiteSpace || 0) * dpr;
  return {
    x: (rect.x - offset.offsetX) * scale * dpr + ws,
    y: (rect.y - offset.offsetY) * scale * dpr + ws,
    width: rect.width * scale * dpr,
    height: rect.height * scale * dpr
  };
}

/**
 * 合并业务矩形边界。
 * @param {{minX:number,minY:number,maxX:number,maxY:number}} bounds - 当前边界
 * @param {Object|null} rect - 待合并矩形
 * @returns {void}
 */
function mergeMaskRectBounds(bounds, rect) {
  // 无效矩形不参与外轮廓扩展。
  if (!rect || !isFinite(rect.x) || !isFinite(rect.y)) return;
  if (!isFinite(rect.width) || !isFinite(rect.height)) return;
  bounds.minX = Math.min(bounds.minX, rect.x);
  bounds.minY = Math.min(bounds.minY, rect.y);
  bounds.maxX = Math.max(bounds.maxX, rect.x + rect.width);
  bounds.maxY = Math.max(bounds.maxY, rect.y + rect.height);
}

/**
 * 获取孔位/组件区域的业务坐标外接矩形。
 * @param {Object} region - 孔位/组件区域
 * @param {Object} sharedState - 共享状态
 * @returns {{x:number,y:number,width:number,height:number}|null} 业务坐标矩形
 */
function getHoleRegionMaskRect(region, sharedState) {
  // 缺少中心点时无法把组件区域转换到业务坐标。
  if (!region || !isFinite(region.cx) || !isFinite(region.cy)) {
    return null;
  }
  if (region.kind === 'rotatedRect') {
    var halfW = (region.w || 0) / 2;
    var halfH = (region.h || 0) / 2;
    var angle = region.rotationRad || 0;
    var cos = Math.cos(angle);
    var sin = Math.sin(angle);
    var points = [
      { x: -halfW, y: -halfH },
      { x: halfW, y: -halfH },
      { x: halfW, y: halfH },
      { x: -halfW, y: halfH }
    ];
    var bounds = {
      minX: Infinity,
      minY: Infinity,
      maxX: -Infinity,
      maxY: -Infinity
    };
    for (var i = 0; i < points.length; i++) {
      var p = points[i];
      var backendPoint = convertCanvasToBackend(
        {
          x: region.cx + p.x * cos - p.y * sin,
          y: region.cy + p.x * sin + p.y * cos
        },
        sharedState
      );
      bounds.minX = Math.min(bounds.minX, backendPoint.x);
      bounds.minY = Math.min(bounds.minY, backendPoint.y);
      bounds.maxX = Math.max(bounds.maxX, backendPoint.x);
      bounds.maxY = Math.max(bounds.maxY, backendPoint.y);
    }
    return {
      x: bounds.minX,
      y: bounds.minY,
      width: bounds.maxX - bounds.minX,
      height: bounds.maxY - bounds.minY
    };
  }
  if (region.radius != null) {
    var center = convertCanvasToBackend(
      { x: region.cx, y: region.cy },
      sharedState
    );
    var radius = region.radius / (sharedState.canvasScale || 1);
    return {
      x: center.x - radius,
      y: center.y - radius,
      width: radius * 2,
      height: radius * 2
    };
  }
  return null;
}

/**
 * 计算添加组件后的 mask 外轮廓业务矩形。
 * @param {Object} sharedState - 共享状态
 * @param {Array<Object>} holeRegions - 孔位/组件区域
 * @returns {{x:number,y:number,width:number,height:number}|null} 业务坐标矩形
 */
function getImageBorderParseReturnMaskRect(sharedState, holeRegions) {
  var baseRect = imageBorderParseRectToMaskRect(
    sharedState.imageBorderParseRect
  );
  // 基础 rect 缺失时，不能可靠表达 res.mask 的业务外轮廓。
  if (!baseRect) return null;
  var bounds = {
    minX: baseRect.x,
    minY: baseRect.y,
    maxX: baseRect.x + baseRect.width,
    maxY: baseRect.y + baseRect.height
  };
  var list = holeRegions || [];
  for (var i = 0; i < list.length; i++) {
    mergeMaskRectBounds(bounds, getHoleRegionMaskRect(list[i], sharedState));
  }
  return {
    x: bounds.minX,
    y: bounds.minY,
    width: bounds.maxX - bounds.minX,
    height: bounds.maxY - bounds.minY
  };
}

/**
 * 将 canvas 导出为 PNG blobUrl。
 * @param {HTMLCanvasElement} canvas - 待导出的 canvas
 * @returns {Promise<string>} PNG blobUrl
 */
function canvasToObjectUrl(canvas) {
  return new Promise(function (resolve, reject) {
    canvas.toBlob(function (blob) {
      if (blob) resolve(URL.createObjectURL(blob));
      else reject(new Error('canvas.toBlob 失败'));
    }, 'image/png');
  });
}

/**
 * 释放导出链路内部生成但未交给调用方持有的 Blob URL。
 * @param {*} url - 需要释放的 URL。
 * @returns {void}
 */
function revokeExportBlobUrl(url) {
  // 非 Blob URL 不属于当前导出链路创建的运行时地址。
  if (typeof url !== 'string' || url.indexOf('blob:') !== 0) {
    return;
  }
  URL.revokeObjectURL(url);
}

/**
 * 按业务矩形对应的原始 canvas 区域导出组件后 mask。
 * @param {Object} opt - 参数对象
 * @param {HTMLCanvasElement} opt.sourceCanvas - 组件后 mask 原始 canvas
 * @param {Object} opt.sourceRect - 原始 canvas 物理坐标矩形
 * @param {Object} opt.maskRect - 业务坐标矩形
 * @returns {Promise<string>} 组件后 mask 图片 blobUrl
 */
async function exportComponentMaskByRect({
  sourceCanvas,
  sourceRect,
  maskRect
}) {
  var exportCanvas = document.createElement('canvas');
  exportCanvas.width = Math.max(1, Math.round(maskRect.width));
  exportCanvas.height = Math.max(1, Math.round(maskRect.height));
  var exportCtx = exportCanvas.getContext('2d');
  exportCtx.drawImage(
    sourceCanvas,
    sourceRect.x,
    sourceRect.y,
    sourceRect.width,
    sourceRect.height,
    0,
    0,
    exportCanvas.width,
    exportCanvas.height
  );
  return canvasToObjectUrl(exportCanvas);
}

/**
 * 将输入 mask 绘制到 serviceComponentHole 原始 canvas 并叠加组件孔位信息。
 * @param {Object} opt - 参数对象
 * @param {string} opt.imageSrc - 输入 mask 图片
 * @param {Object} opt.sharedState - 共享状态
 * @param {Array<Object>} opt.holeRegions - 当前孔位区域
 * @param {boolean} opt.addKeyHoleOuterCircle - 是否补绘钥匙扣外圆
 * @param {boolean} [opt.preserveSourceRatio=false] - 是否保留输入 mask 图片宽高比例
 * @returns {Promise<HTMLCanvasElement>} 组件后的原始物理 canvas
 */
async function createMaskCanvasWithComponents({
  imageSrc,
  sharedState,
  holeRegions,
  addKeyHoleOuterCircle,
  preserveSourceRatio = false
}) {
  var dpr = sharedState.dpr;
  var canvasWidth = sharedState.canvas.width;
  var canvasHeight = sharedState.canvas.height;
  var offCanvas = document.createElement('canvas');
  offCanvas.width = canvasWidth;
  offCanvas.height = canvasHeight;
  var offCtx = offCanvas.getContext('2d');

  offCtx.fillStyle = '#000000';
  offCtx.fillRect(0, 0, canvasWidth, canvasHeight);

  var imgB = await loadImageElement(imageSrc);
  var baseDrawW = sharedState.imageInfo.width * sharedState.canvasScale * dpr;
  var baseDrawH = sharedState.imageInfo.height * sharedState.canvasScale * dpr;
  var drawW = baseDrawW;
  var drawH = baseDrawH;
  // 组件后原始 mask 口径需要保留输入图片比例，立牌 raw mask 高度可能超过业务高度。
  if (preserveSourceRatio && imgB.width && imgB.height) {
    drawH = imgB.height * (drawW / imgB.width);
  }
  var drawX = (canvasWidth - baseDrawW) / 2;
  var drawY = (canvasHeight - baseDrawH) / 2;
  offCtx.drawImage(imgB, drawX, drawY, drawW, drawH);

  var ws = sharedState.canvasRoundWhiteSpace * dpr;
  var list = holeRegions || [];

  // 钥匙扣外圆是 mask 的保留区域，需要额外填白。
  if (addKeyHoleOuterCircle) {
    offCtx.fillStyle = '#FFFFFF';
    for (var i = 0; i < list.length; i++) {
      var h = list[i];
      if (h.type !== DRAW_TYPE.KEY_HOLE) continue;
      offCtx.beginPath();
      offCtx.arc(
        h.cx * dpr + ws,
        h.cy * dpr + ws,
        h.radius * dpr,
        0,
        Math.PI * 2
      );
      offCtx.fill();
    }
    offCtx.fillStyle = '#000000';
    for (var i2 = 0; i2 < list.length; i2++) {
      var h2 = list[i2];
      if (h2.type !== DRAW_TYPE.KEY_HOLE || !h2.inRadius) continue;
      offCtx.beginPath();
      offCtx.arc(
        h2.cx * dpr + ws,
        h2.cy * dpr + ws,
        h2.inRadius * dpr,
        0,
        Math.PI * 2
      );
      offCtx.fill();
    }
  }

  offCtx.fillStyle = '#000000';
  for (var j = 0; j < list.length; j++) {
    var hr = list[j];
    if (hr.type === DRAW_TYPE.KEY_HOLE) continue;
    if (hr.kind === 'circle') {
      offCtx.beginPath();
      offCtx.arc(
        hr.cx * dpr + ws,
        hr.cy * dpr + ws,
        hr.radius * dpr,
        0,
        Math.PI * 2
      );
      offCtx.fill();
    } else if (hr.kind === 'rotatedRect') {
      offCtx.save();
      offCtx.translate(hr.cx * dpr + ws, hr.cy * dpr + ws);
      offCtx.rotate(hr.rotationRad);
      buildRoundRectPath(
        offCtx,
        (-hr.w * dpr) / 2,
        (-hr.h * dpr) / 2,
        hr.w * dpr,
        hr.h * dpr,
        computeBaseHoleCornerRadius(
          hr.w * dpr,
          hr.h * dpr,
          hr.cornerRadiusPercent
        )
      );
      offCtx.fill();
      offCtx.restore();
    }
  }
  return offCanvas;
}

/**
 * 按裁剪边界导出 blobUrl 图片。
 * @param {string} blobUrl - 原始 canvas 导出的 blobUrl
 * @param {Object} cropBounds - 裁剪边界
 * @param {Object} sharedState - 共享状态
 * @returns {Promise<string>}
 */
async function exportImageByCropBounds(blobUrl, cropBounds, sharedState) {
  var canvasWidth = sharedState.canvas.width;
  var canvasHeight = sharedState.canvas.height;
  var leftWidth = cropBounds.leftWidth;
  var topWidth = cropBounds.topWidth;
  var rightWidth = cropBounds.rightWidth;
  var bottomWidth = cropBounds.bottomWidth;
  var cropWidth = canvasWidth - leftWidth - rightWidth;
  var cropHeight = canvasHeight - topWidth - bottomWidth;
  var img = new Image();
  await new Promise(function (resolve, reject) {
    img.onload = resolve;
    img.onerror = reject;
    img.src = blobUrl;
  });
  var exportCanvas = document.createElement('canvas');
  exportCanvas.width = cropWidth;
  exportCanvas.height = cropHeight;
  var exportCtx = exportCanvas.getContext('2d');
  exportCtx.drawImage(
    img,
    leftWidth,
    topWidth,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );
  return new Promise(function (resolve, reject) {
    exportCanvas.toBlob(function (exportBlob) {
      if (exportBlob) resolve(URL.createObjectURL(exportBlob));
      else reject(new Error('exportCanvas.toBlob 失败'));
    }, 'image/png');
  });
}

/**
 * 初始化多部件孔位服务。
 *
 * @param {Object} params
 * @param {string} params.imageSrc - 必填，用户图 URL。edit.vue: outlineOriginImageSrc（noBgRes.url，canvas 导出无背景图再上传后的 url）
 * @param {Array<Array<number>>} params.outline - 必填，imageBorderParse 返回的轮廓。edit.vue: res.outline
 * @param {Array<Array<number>>} [params.renderOutline] - 渲染用外轮廓点集。edit.vue: res.pathList
 * @param {number} [params.erode=0] - edit.vue: res.erode
 * @param {string} [params.mask] - edit.vue: res.mask（经 formatMaskUrl 转 dataURL）
 * @param {Object} params.canvasOptions
 * @param {HTMLCanvasElement} params.canvasOptions.canvas - multipleComponent: DOM canvas 元素；edit.vue draw 模式不传（内部自动创建离屏 canvas）
 * @param {HTMLCanvasElement} [params.canvasOptions.scaleCanvas] - multipleComponent: DOM canvas 元素；edit.vue draw 模式不传
 * @param {number} params.canvasOptions.maxCanvasWidth - 默认 2000，与旧 serviceHole 一致
 * @param {number} params.canvasOptions.maxCanvasHeight - 默认 2000，与旧 serviceHole 一致
 * @param {Object} params.serviceConfig
 * @param {string} [params.serviceConfig.mode='edit'] - multipleComponent: 'edit'；edit.vue draw 模式: 'draw'
 * @param {number} [params.serviceConfig.strokeWidth] - edit.vue: this.getStokeWidth()
 * @param {number} [params.serviceConfig.componentMinGapMm=0] - 外部传入，组件间最小间隙 mm
 * @param {boolean} [params.serviceConfig.enableMagnifierGlobal=true]
 * @param {boolean} [params.serviceConfig.disableTouchEvent=false]
 * @param {Object} [params.serviceConfig.maxCount] - 外部传入覆盖 DEFAULT_CONFIG
 * @param {Object} params.serviceConfig.sizePresets - 必传，页面/API 注入的规格预设表，用于计算最大钥匙扣留白
 * @param {Object} [params.serviceConfig.imageBorderParseParams] - 立牌场景必传。multipleComponent: { fileName, paperWidth, paperHeight, strokeWidth, mask }
 * @param {Array<number>} [params.serviceConfig.imageBorderParseRect] - imageBorderParse 返回的 rect，格式为 [x, y, width, height]
 * @param {Object} [params.serviceConfig.restoreFromLayerData] - 二次编辑还原的组件数据。edit.vue: item.layerData.componentData（multipleComponent 确认后回传的 *List 数据），首次渲染 componentData 为空时退化为 item.layerData
 * @param {Function} [params.serviceConfig.showLoading] - 页面级 loading 显示方法
 * @param {Function} [params.serviceConfig.hideLoading] - 页面级 loading 关闭方法
 * @returns {Promise<Object>} publicApi
 *
 * @example
 * const api = await initMultiComponent({
 *   imageSrc: query.url,
 *   outline: res.outline,
 *   renderOutline: res.pathList,
 *   erode: res.erode,
 *   mask: 'data:image/png;base64,' + res.mask,
 *   canvasOptions: {
 *     canvas: document.getElementById('contourCanvas'),
 *     scaleCanvas: document.getElementById('scaleCanvas'),
 *     maxCanvasWidth: outer.offsetWidth,
 *     maxCanvasHeight: outer.offsetHeight
 *   },
 *   serviceConfig: {
 *     mode: 'edit',
 *     strokeWidth: 2,
 *     componentMinGapMm: 0,
 *     sizePresets: panelSizePresets
 *   }
 * });
 * await api.addComponent('keyHole', {
 *   geometryConfig: { outerHoleDiameterMm: 6, innerHoleDiameterMm: 2.5 }
 * });
 */
export async function initMultiComponent(params) {
  const p = params || {};
  validateInitParams({ imageSrc: p.imageSrc, outline: p.outline });
  const serviceConfig = p.serviceConfig || {};

  // imageBorderParseParams 必传：立牌组件的刀线刷新依赖此参数
  if (
    !serviceConfig.imageBorderParseParams ||
    !serviceConfig.imageBorderParseParams.fileName
  ) {
    throw new Error(
      '[serviceComponentHole] serviceConfig.imageBorderParseParams 为必填参数，需包含 fileName/paperWidth/paperHeight/strokeWidth/mask'
    );
  }

  // sizePresets 必传：canvas 留白依赖页面/API 规格，避免大钥匙扣导出时被裁剪。
  if (
    !serviceConfig.sizePresets ||
    typeof serviceConfig.sizePresets !== 'object' ||
    Array.isArray(serviceConfig.sizePresets)
  ) {
    throw new Error(
      '[serviceComponentHole] serviceConfig.sizePresets 为必填参数，需传入规格预设表'
    );
  }

  const canvasOptions = p.canvasOptions || {};

  const template = Store.get('skuTemplate') || {};

  const sharedState = await initSharedCanvasState({
    imageSrc: p.imageSrc,
    outline: p.outline,
    renderOutline: p.renderOutline,
    erode: p.erode,
    mask: p.mask,
    canvas: canvasOptions.canvas,
    scaleCanvas: canvasOptions.scaleCanvas,
    maxCanvasWidth: canvasOptions.maxCanvasWidth,
    maxCanvasHeight: canvasOptions.maxCanvasHeight,
    template,
    mode: serviceConfig.mode || modeEdit,
    strokeWidth: serviceConfig.strokeWidth,
    imageBorderParseParams: serviceConfig.imageBorderParseParams,
    imageBorderParseRect: serviceConfig.imageBorderParseRect,
    sizePresets: serviceConfig.sizePresets,
    isCustomSkuSize: !!serviceConfig.isCustomSkuSize,
    debug: !!p.debug
  });

  const orchestrator = createOrchestrator(sharedState, serviceConfig);
  orchestrator.bindEvents();

  // 还原 layerData（如果传了）
  if (serviceConfig.restoreFromLayerData) {
    const restoreList = layerDataToRestoreList(
      serviceConfig.restoreFromLayerData
    );
    if (restoreList.length) {
      await orchestrator.restoreComponents(restoreList);
    }
  }

  // 首次绘制
  await orchestrator.update();

  // 设置 dirty baseline（restoreComponents 内部已经设过；若没还原则在这里设空 baseline）
  if (!serviceConfig.restoreFromLayerData) {
    orchestrator.setDirtyBaseline();
  }

  return buildPublicApi(orchestrator, sharedState);
}

/**
 * 把 layerData 中的 *List + origin*List 合成 restoreComponents 入参。
 * 阶段 1 只识别 keyHole；阶段 2-4 扩展。
 * @param {Object} layerData
 * @returns {Array<Object>}
 */
function layerDataToRestoreList(layerData) {
  const list = [];
  // keyHole
  if (
    layerData.originCenterPointList &&
    layerData.originCenterPointList.length
  ) {
    layerData.originCenterPointList.forEach((item) => {
      list.push({
        type: DRAW_TYPE.KEY_HOLE,
        geometryConfig: item.geometryConfig,
        placement: { centerPoint: item },
        sizePresetId: item.sizePresetId,
        enableMagnifier: item.enableMagnifier
      });
    });
  }
  // liPai
  if (layerData.originSocketInfoList && layerData.originSocketInfoList.length) {
    layerData.originSocketInfoList.forEach((item, index) => {
      const socketInfoItem = Array.isArray(layerData.socketInfoList)
        ? layerData.socketInfoList[index]
        : null;
      const slotRect =
        item.slotRect || (socketInfoItem && socketInfoItem.slotRect);
      let slotOffsetRatio = null;
      // 新版数据优先用独立偏移字段恢复插口位置。
      if (typeof item.slotOffsetRatio === 'number') {
        slotOffsetRatio = item.slotOffsetRatio;
      } else {
        // 兼容旧数据中写在 slotRect[6] 的插口偏移比例。
        if (Array.isArray(slotRect) && typeof slotRect[6] === 'number') {
          slotOffsetRatio = slotRect[6];
        }
      }
      list.push({
        type: DRAW_TYPE.LI_PAI,
        geometryConfig: item.geometryConfig,
        placement: {
          originSocketInfo: {
            point: item.point,
            width: item.width,
            height: item.height,
            containerPoint: item.containerPoint,
            containerWidth: item.containerWidth,
            containerHeight: item.containerHeight,
            slotRect,
            slotOffsetRatio,
            baseCanvasWidth: item.baseCanvasWidth,
            canvasRoundWhiteSpace: item.canvasRoundWhiteSpace
          }
        },
        sizePresetId: item.sizePresetId,
        enableMagnifier: item.enableMagnifier
      });
    });
  }
  // perforation
  if (
    layerData.originPerforationPointList &&
    layerData.originPerforationPointList.length
  ) {
    layerData.originPerforationPointList.forEach((item) => {
      list.push({
        type: DRAW_TYPE.PERFORATION,
        geometryConfig: item.geometryConfig,
        placement: { centerPoint: item },
        sizePresetId: item.sizePresetId,
        enableMagnifier: item.enableMagnifier
      });
    });
  }
  // baseHole
  if (
    layerData.originBaseHoleInfoList &&
    layerData.originBaseHoleInfoList.length
  ) {
    layerData.originBaseHoleInfoList.forEach((item) => {
      list.push({
        type: DRAW_TYPE.BASE_HOLE,
        geometryConfig: item.geometryConfig,
        placement: {
          centerPoint: {
            x: item.centerPoint.x,
            y: item.centerPoint.y,
            baseCanvasWidth: item.baseCanvasWidth,
            canvasRoundWhiteSpace: item.canvasRoundWhiteSpace
          },
          rotationRad: item.rotationRad
        },
        sizePresetId: item.sizePresetId,
        enableMagnifier: item.enableMagnifier
      });
    });
  }
  return list;
}

/**
 * 把 orchestrator + sharedState 包装成对外稳定的 publicApi。
 * @param {Object} orchestrator
 * @param {Object} sharedState
 * @returns {Object}
 */
function buildPublicApi(orchestrator, sharedState) {
  return {
    addComponent: orchestrator.addComponent,
    removeComponent: orchestrator.removeComponent,
    restoreComponents: orchestrator.restoreComponents,
    setSelectedId: orchestrator.setSelectedId,
    getComponents: orchestrator.getComponents,
    getMaxCount: orchestrator.getMaxCount,
    setComponentMagnifier: orchestrator.setComponentMagnifier,
    switchMode: orchestrator.switchMode,
    hasConflict: orchestrator.hasConflict,
    getConflictIds: orchestrator.getConflictIds,
    isDirty: orchestrator.isDirty,
    exportLayerDataPatch: orchestrator.exportLayerDataPatch,
    /**
     * 获取当前 mask（可能被立牌刀线刷新后更新为含槽口的新 mask）。
     * @returns {string}
     */
    getMask() {
      return sharedState.mask;
    },
    /**
     * 获取自定义尺寸 SKU 导出时使用的新后端坐标矩形。
     * @returns {{x:number,y:number,width:number,height:number,originalWidth:number,originalHeight:number}}
     */
    getCustomSkuBackendBounds() {
      var bounds = getCustomSkuBackendBounds(
        orchestrator.getComponents(),
        sharedState
      );
      return {
        ...bounds,
        originalWidth: sharedState.imageInfo.width,
        originalHeight: sharedState.imageInfo.height
      };
    },
    /**
     * 获取自定义尺寸 SKU 使用的添加组件前后刀线后端坐标矩形。
     * @returns {{outlineBounds:Object|null,componentOutlineBounds:Object|null,basis:Object,imageBorderParseRect:Array<number>|null,imageSrc:string,imageInfo:Object|null}}
     */
    getCustomSkuOutlineBounds() {
      return getCustomSkuOutlineBounds(
        orchestrator.getHoleRegions(),
        sharedState
      );
    },
    /**
     * 获取调试快照，用于排查插口坐标与 slotRect 换算问题。
     * @returns {Object} 可序列化的调试数据
     */
    getDebugSnapshot() {
      return buildDebugSnapshot(orchestrator, sharedState);
    },
    update: orchestrator.update,
    destroy: orchestrator.destroy,
    /**
     * 注册 canvas 选中变化回调（仅 canvas 交互触发，避免与 setSelectedId 循环）。
     * @param {Function|null} fn - 回调函数，参数为 componentId
     */
    onSelectedChange: orchestrator.setOnSelectedChange,
    /**
     * 导出当前 canvas 为 PNG blobUrl，并裁掉外围透明区域。
     * @param {Object} [opt] - 导出选项
     * @param {Object} [opt.cropBounds] - 手动裁剪边界 { leftWidth, topWidth, rightWidth, bottomWidth }（物理像素）
     * @param {boolean} [opt.returnCropBounds] - 是否同步返回裁剪边界
     * @param {string} [opt.lineColor] - 刀线颜色，传入时先重绘再导出
     * @returns {Promise<{url:string, cropBounds?:Object}>}
     */
    exportImage(opt) {
      var options = opt || {};
      var optLineColor = options.lineColor;
      var oldLineColor = sharedState.lineColor;
      var cropBounds = options.cropBounds;
      var returnOutlineSourceCanvasInfo = !!options.returnOutlineSourceCanvasInfo;
      return (async function () {
        // 指定导出刀线颜色时先重绘，保持与 serviceHoleNew.exportImage 一致
        if (optLineColor !== undefined) {
          sharedState.lineColor = optLineColor;
          await orchestrator.update();
        }
        try {
          return await new Promise((resolve, reject) => {
            sharedState.canvas.toBlob(async (blob) => {
              let blobUrl = '';
              try {
                if (!blob) {
                  reject(new Error('canvas.toBlob 失败'));
                  return;
                }
                blobUrl = URL.createObjectURL(blob);
                // 复用已有裁剪边界时跳过透明区域扫描，避免透明刀线影响裁剪结果
                if (cropBounds) {
                  var canvasWidth = sharedState.canvas.width;
                  var canvasHeight = sharedState.canvas.height;
                  var leftWidth = cropBounds.leftWidth;
                  var topWidth = cropBounds.topWidth;
                  var rightWidth = cropBounds.rightWidth;
                  var bottomWidth = cropBounds.bottomWidth;
                  var cropWidth = canvasWidth - leftWidth - rightWidth;
                  var cropHeight = canvasHeight - topWidth - bottomWidth;
                  var img = new Image();
                  await new Promise(function (res, rej) {
                    img.onload = res;
                    img.onerror = rej;
                    img.src = blobUrl;
                  });
                  var exportCanvas = document.createElement('canvas');
                  exportCanvas.width = cropWidth;
                  exportCanvas.height = cropHeight;
                  var exportCtx = exportCanvas.getContext('2d');
                  exportCtx.drawImage(
                    img,
                    leftWidth,
                    topWidth,
                    cropWidth,
                    cropHeight,
                    0,
                    0,
                    cropWidth,
                    cropHeight
                  );
                  var exportUrl = await new Promise(function (res, rej) {
                    exportCanvas.toBlob(function (exportBlob) {
                      if (exportBlob) res(URL.createObjectURL(exportBlob));
                      else rej(new Error('exportCanvas.toBlob 失败'));
                    }, 'image/png');
                  });
                  var resolvedResult = {
                    url: exportUrl,
                    cropBounds: cropBounds
                  };
                  if (returnOutlineSourceCanvasInfo) {
                    resolvedResult.outlineSourceCanvasInfo = getOutlineSourceCanvasInfo(
                      sharedState
                    );
                  }
                  resolve(resolvedResult);
                  return;
                }
                var exportCropBounds = getExportCropBounds(
                  sharedState,
                  orchestrator.getHoleRegions()
                );
                var resultUrl = await exportImageByCropBounds(
                  blobUrl,
                  exportCropBounds,
                  sharedState
                );
                var result = {
                  url: resultUrl,
                  cropBounds: exportCropBounds
                };
                if (returnOutlineSourceCanvasInfo) {
                  result.outlineSourceCanvasInfo = getOutlineSourceCanvasInfo(
                    sharedState
                  );
                }
                resolve(result);
              } catch (e) {
                reject(e);
              } finally {
                // 源 canvas blobUrl 只作为本函数裁剪输入，成功和异常路径都释放。
                if (blobUrl) {
                  revokeExportBlobUrl(blobUrl);
                }
              }
            }, 'image/png');
          });
        } finally {
          // 临时刀线颜色只影响本次导出，导出完成后恢复画布显示
          if (optLineColor !== undefined) {
            sharedState.lineColor = oldLineColor;
            await orchestrator.update();
          }
        }
      })();
    },
    /**
     * 导出自定义尺寸坐标矩形调试图。
     * 绿色为含描边 outline 矩形，黑色为扣描边后的最小描边矩形，蓝色为组件外扩矩形。
     * @returns {Promise<string>} blob URL
     */
    exportCustomSkuBoundsDebugImage() {
      return new Promise(function (resolve, reject) {
        try {
          var dpr = sharedState.dpr || 1;
          var ws = sharedState.canvasRoundWhiteSpace * dpr;
          var offCanvas = document.createElement('canvas');
          offCanvas.width = sharedState.canvas.width;
          offCanvas.height = sharedState.canvas.height;
          var offCtx = offCanvas.getContext('2d');
          offCtx.drawImage(sharedState.canvas, 0, 0);

          var customBounds = getCustomSkuOutlineBounds(
            orchestrator.getHoleRegions(),
            sharedState
          );
          var originalRect = customBounds.outlineBounds;
          var minOutlineRect = getCustomSkuMinOutlineBounds(customBounds);
          var customRect = customBounds.componentOutlineBounds;

          /**
           * 绘制后端坐标矩形。
           * @param {Object} rect - 后端坐标矩形
           * @param {string} color - 矩形颜色
           * @returns {void}
           */
          function drawBackendRect(rect, color) {
            offCtx.save();
            offCtx.strokeStyle = color;
            offCtx.lineWidth = 2 * dpr;
            offCtx.strokeRect(
              rect.x * sharedState.canvasScale * dpr + ws,
              rect.y * sharedState.canvasScale * dpr + ws,
              rect.width * sharedState.canvasScale * dpr,
              rect.height * sharedState.canvasScale * dpr
            );
            offCtx.restore();
          }

          // outline 矩形存在时绘制绿色基准框。
          if (originalRect) {
            drawBackendRect(originalRect, '#00b050');
          }
          // 最小描边矩形存在时绘制黑色扣描边框。
          if (minOutlineRect) {
            drawBackendRect(minOutlineRect, '#000000');
          }
          // 组件外扩矩形存在时绘制蓝色结果框。
          if (customRect) {
            drawBackendRect(customRect, '#0070c0');
          }

          offCanvas.toBlob(function (blob) {
            if (!blob) {
              reject(new Error('debug canvas.toBlob 失败'));
              return;
            }
            resolve(URL.createObjectURL(blob));
          }, 'image/png');
        } catch (error) {
          reject(error);
        }
      });
    },
    /**
     * 以外部图作为底图，套用当前组件几何重新合成（用于钥匙扣 mask 处理等）。
     * 阶段 1 暂未实现，预留接口避免外部调用报错。
     * @param {string} imageBSrc
     * @param {Object} [opt]
     * @returns {Promise<null>}
     */
    /**
     * 以外部图作为底图，套用当前组件几何重新合成。
     * 钥匙扣：addKeyHoleOuterCircle 为 true 时在 mask 上补绘白色孔位外圆，
     * 使后续 composeBgWithOutlineImage 在孔位外圈保留背景。
     * `returnComponentMaskInfo` 的对象返回结构必须与 serviceHoleNew.exportImageBySrc 对齐。
     * @param {string} imageBSrc - 基础 mask 图源
     * @param {Object} [opt]
     * @param {boolean} [opt.addKeyHoleOuterCircle=false]
     * @param {boolean} [opt.returnComponentMaskInfo=false] - 是否返回组件后 mask 信息
     * @returns {Promise<string|Object>} blob URL；returnComponentMaskInfo=true 时返回对象
     */
    async exportImageBySrc(imageBSrc, opt) {
      const addKeyHoleOuterCircle = !!(opt && opt.addKeyHoleOuterCircle);
      const returnComponentMaskInfo = !!(opt && opt.returnComponentMaskInfo);
      const hasLiPaiComponent = orchestrator
        .getComponents()
        .some(function (item) {
          return item.type === DRAW_TYPE.LI_PAI;
        });
      var finalImageBSrc = imageBSrc;
      var shouldRevokeFinalImageBSrc = false;

      // 立牌接口返回的 mask 可能因底部插口扩展而不是 SKU 比例，需要先补黑边归一化。
      if (hasLiPaiComponent) {
        finalImageBSrc = await normalizeMaskToSkuRatio(
          imageBSrc,
          sharedState.imageInfo.width / sharedState.imageInfo.height
        );
        shouldRevokeFinalImageBSrc = finalImageBSrc !== imageBSrc;
        debugLog({
          title: '立牌mask归一化结果',
          originMaskUrl: imageBSrc,
          normalizedMaskUrl: finalImageBSrc,
          isNormalized: shouldRevokeFinalImageBSrc
        });
      }

      var canvasWidth = sharedState.canvas.width;
      var canvasHeight = sharedState.canvas.height;
      var allHoleRegions = orchestrator.getHoleRegions();
      var cropRes = getExportCropBounds(sharedState, allHoleRegions);
      var componentMaskUrl = '';
      var imageBorderParseReturnMaskRect = null;
      var imageBorderParseReturnMaskRectInSourceCanvas = null;
      var resultUrl = '';
      var shouldRevokeReturnedBlobUrls = true;

      try {
        // 新链路需要未归一化的组件后 mask，默认旧返回值仍保持原有归一化逻辑。
        if (returnComponentMaskInfo) {
          imageBorderParseReturnMaskRect = getImageBorderParseReturnMaskRect(
            sharedState,
            allHoleRegions
          );
          // 基础 rect 有效时，才导出可被背景坐标系映射的组件后 mask。
          if (imageBorderParseReturnMaskRect) {
            var componentMaskCanvas = await createMaskCanvasWithComponents({
              imageSrc: imageBSrc,
              sharedState,
              holeRegions: allHoleRegions,
              addKeyHoleOuterCircle,
              preserveSourceRatio: true
            });
            imageBorderParseReturnMaskRectInSourceCanvas = backendRectToSourceCanvasRect(
              imageBorderParseReturnMaskRect,
              sharedState
            );
            componentMaskUrl = await exportComponentMaskByRect({
              sourceCanvas: componentMaskCanvas,
              sourceRect: imageBorderParseReturnMaskRectInSourceCanvas,
              maskRect: imageBorderParseReturnMaskRect
            });
          }
        }

        var offCanvas = await createMaskCanvasWithComponents({
          imageSrc: finalImageBSrc,
          sharedState,
          holeRegions: allHoleRegions,
          addKeyHoleOuterCircle
        });
        // 按 exportImage 相同的刀线几何边界裁剪
        var leftWidth = cropRes.leftWidth;
        var topWidth = cropRes.topWidth;
        var rightWidth = cropRes.rightWidth;
        var bottomWidth = cropRes.bottomWidth;

        var cropWidth = canvasWidth - leftWidth - rightWidth;
        var cropHeight = canvasHeight - topWidth - bottomWidth;

        var exportCanvas = document.createElement('canvas');
        exportCanvas.width = cropWidth;
        exportCanvas.height = cropHeight;
        var exportCtx = exportCanvas.getContext('2d');
        exportCtx.drawImage(
          offCanvas,
          leftWidth,
          topWidth,
          cropWidth,
          cropHeight,
          0,
          0,
          cropWidth,
          cropHeight
        );

        var exportUrl = await canvasToObjectUrl(exportCanvas);
        resultUrl = exportUrl;

        // 临时修复：立牌 mask 最后按白色像素最小矩形裁剪，并补黑边归一化为 SKU 比例。
        if (hasLiPaiComponent) {
          var fixedUrl = await cropWhiteMaskToSkuRatio(
            exportUrl,
            sharedState.imageInfo.width / sharedState.imageInfo.height
          );
          // 裁剪归一化返回新 URL 时，旧导出图不再被返回。
          if (fixedUrl !== exportUrl) {
            revokeExportBlobUrl(exportUrl);
          }
          resultUrl = fixedUrl;
        }

        // 默认保持历史返回值，避免影响旧调用方。
        if (!returnComponentMaskInfo) {
          shouldRevokeReturnedBlobUrls = false;
          return resultUrl;
        }
        shouldRevokeReturnedBlobUrls = false;
        return {
          url: resultUrl,
          componentMaskUrl,
          imageBorderParseReturnMaskRect,
          imageBorderParseReturnMaskRectInSourceCanvas
        };
      } finally {
        // 归一化产生的临时 blobUrl 只用于本次绘制，成功和异常路径都释放。
        if (shouldRevokeFinalImageBSrc) {
          revokeExportBlobUrl(finalImageBSrc);
        }
        // 异常路径下，已生成但未返回给调用方的导出 URL 需要释放。
        if (shouldRevokeReturnedBlobUrls) {
          revokeExportBlobUrl(resultUrl);
          revokeExportBlobUrl(componentMaskUrl);
        }
      }
    }
  };
}

export { DRAW_TYPE };
export {
  convertAcrylicToComponentData,
  convertComponentDataToAcrylic
} from './presetConverter.js';
